import React, { useState } from "react";

import PlanCategorySelectionCards from "./PlanCategorySelectionCards";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { userActivityLogger } from "../../../utils/mixpanel";
import { IPetItemResponse } from "../../../services/pet-list/pet-list.interface";
import { IPlanOption, planPricingType } from "../../../hooks/useWellnessPlans";
import { getSpeciesName } from "../../../components/PageTitle/WellnessPlanTitle/WellnessPlanTitle";

export interface IPlanDetail {
  planName: string;
  planValue: string;
  planPrice: number;
  defaultPlan: boolean;
  planType: string;
  ageGroup?: string;
}

interface IPlanCategorySelectionCardList {
  planDetails: IPlanOption[];
  selectedPlan: string;
  setSelectedplan: (planName: string) => void;
  currentPetDetails: IPetItemResponse;
  currentPlanType: string;
  planPricing: planPricingType;
  annualOnly?: boolean;
}

const isBasePlan = (plan: IPlanOption) => plan.planType === "base";

const PlanCategorySelectionCardList = ({
  planDetails,
  selectedPlan,
  setSelectedplan,
  currentPetDetails,
  currentPlanType,
  planPricing,
  annualOnly = false,
}: IPlanCategorySelectionCardList) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handlePlanCardSelction = (planValue: string, planType: string) => {
    const formattedBirthdate = currentPetDetails.birthdate ?
      new Date(
        parseInt(currentPetDetails.birthdate) * 1000,
      ).toLocaleDateString("en-US") :
      null;

    userActivityLogger(MIXPANEL_EVENTS.USER_WP_PLAN_TOGGLE, {
      pet_id: currentPetDetails.id,
      pet_name: currentPetDetails.name,
      dob: formattedBirthdate,
      from_plan: currentPlanType,
      to_plan: planType,
      species: getSpeciesName(currentPetDetails.species),
    });
    setSelectedplan(planValue);
  };

  const basePlan = planDetails.find((plan) => isBasePlan(plan));
  const nonBasePlans = planDetails.filter((plan) => !isBasePlan(plan));
  const recommendedPlan = nonBasePlans.find((plan) => plan.recommended);

  const ageGroupOrder = [
    ...new Set([
      recommendedPlan?.ageGroup,
      ...nonBasePlans.map((plan) => plan.ageGroup),
    ].filter(Boolean)),
  ];

  const orderedPlans: IPlanOption[] = [
    ...(basePlan ? [basePlan] : []),
    ...ageGroupOrder.flatMap((ageGroup) =>
      nonBasePlans.filter((plan) => plan.ageGroup === ageGroup),
    ),
  ];

  const surgeryPlan = recommendedPlan ?
    nonBasePlans.find(
      (plan) =>
        plan.ageGroup === recommendedPlan.ageGroup &&
        plan.planValue !== recommendedPlan.planValue,
    ) :
    undefined;

  const initialVisiblePlans = [
    ...(basePlan ? [basePlan] : []),
    ...(recommendedPlan ? [recommendedPlan] : []),
    ...(surgeryPlan ? [surgeryPlan] : []),
  ];

  const visiblePlans = isExpanded ? orderedPlans : initialVisiblePlans;
  const hasMorePlans = orderedPlans.length > initialVisiblePlans.length;

  return (
    <div
      className={`flex flex-col gap-3 w-full ${
        isExpanded ? "md:h-0 md:min-h-full" : ""
      }`}
    >
      <div
        className={`flex flex-col gap-3 w-full ${
          isExpanded ? "md:flex-1 md:min-h-0 md:overflow-y-auto pr-1 scroll-hide" : ""
        }`}
      >
        {visiblePlans.map((planDetail) => (
          <PlanCategorySelectionCards
            key={planDetail.planValue}
            onCardClick={handlePlanCardSelction}
            planDetail={planDetail}
            planPrice={planPricing[planDetail.planValue]}
            planSelected={selectedPlan === planDetail.planValue}
            defaultPlan={planDetail.defaultPlan}
            annualOnly={annualOnly}
          />
        ))}
      </div>
      {!isExpanded && hasMorePlans && (
        <button
          type="button"
          onClick={() => setIsExpanded(true)}
          className="text-sm font-semibold underline hover:underline-offset-2 self-center cursor-pointer"
        >
          See Other Plans
        </button>
      )}
    </div>
  );
};

export default PlanCategorySelectionCardList;
